import React, { useState, useEffect, useRef } from "react";

const InstagramCarousel = ({
  downloaded_path,
  videoRef,
  muted,
  onManualUnmute,
  caption,
  creatorName
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inView, setInView] = useState(true);
  const imageRef = useRef(null);
  const localRef = useRef(null);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev < downloaded_path.length - 1 ? prev + 1 : 0
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : downloaded_path.length - 1
    );
  };

  // Swipe handling
  const handlePointerDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX || e.touches?.[0]?.clientX;
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    const currentX = e.clientX || e.touches?.[0]?.clientX;
    const diff = currentX - startX.current;

    if (Math.abs(diff) > 50) {
      if (diff < 0) nextSlide();
      else prevSlide();
      isDragging.current = false;
    }
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  // Unmute detection
  useEffect(() => {
    const videoEl = localRef.current;
    if (videoRef) videoRef(videoEl);
    if (!videoEl) return;

    const handleVolumeChange = () => {
      if (!videoEl.muted && typeof onManualUnmute === "function") {
        onManualUnmute();
      }
    };

    videoEl.addEventListener("volumechange", handleVolumeChange);
    return () => {
      videoEl.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [videoRef, onManualUnmute]);


  const visibleSlideRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      setInView(entries[0].isIntersecting);
    }, {
      threshold: 0.5,
    });

    const current = visibleSlideRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [currentIndex]);

  useEffect(() => {
    const vid = localRef.current;
    if (vid && vid.tagName === "VIDEO") {
      if (inView) {
        vid.play().then(() => setIsPlaying(true)).catch(() => {});
      } else {
        vid.pause();
        setIsPlaying(false);
      }
    }
  }, [inView]);


  // Play/Pause handler
  const handlePlayPause = () => {
    const videoEl = localRef.current;
    if (videoEl) {
      if (isPlaying) {
        videoEl.pause();
      } else {
        videoEl.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    const videoEl = localRef.current;
    if (videoEl) {
      const newMutedState = !videoEl.muted;
      videoEl.muted = newMutedState;

      if (!newMutedState && typeof onManualUnmute === "function") {
        onManualUnmute();
      }
    }
  };

  // Determine if current media is a video
  const isVideo = () => {
    const ext = downloaded_path[currentIndex]?.split('.').pop().toLowerCase();
    return ["mp4", "webm", "ogg"].includes(ext);
  };

  useEffect(() => {
    const vid = localRef.current;
    if (vid && vid.tagName === "VIDEO") {
      vid.muted = true;
      vid.play().then(() => {
        setIsPlaying(true);
      }).catch((err) => {
        console.warn("Autoplay failed:", err);
      });
    }
  }, [currentIndex]);  

  return (
    <div
      className="relative mx-auto w-[400px] h-auto overflow-hidden"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
    >
      {/* Header */}
      <div className="bg-white flex items-center p-2">
        {/* Profile pic placeholder */}
        <div className="w-8 h-8 bg-gray-200 rounded-full mr-2" />
        <span className="font-bold text-black">{creatorName}</span>
      </div>
      {/* Carousel Wrapper */}
      <div className="relative overflow-hidden">
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {downloaded_path.map((path, index) => (
          <div key={index} className="w-[400px] flex-shrink-0">
            <MediaComponent
              className="w-full object-cover"
              src={`https://genz-staging.feeltiptop.com/${path}`}
              alt={`Slide ${index + 1}`}
              videoRef={index === currentIndex ? localRef : null}
              muted={muted}
              onManualUnmute={onManualUnmute}
              onClick={handlePlayPause}
              isPlaying={isPlaying}
              inView={inView}
              refProp={index === currentIndex ? visibleSlideRef : null}
            />
          </div>
        ))}
      </div>
      <div
        className="absolute inset-0 z-20 cursor-grab"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        onClick={isVideo(downloaded_path[currentIndex]) ? handlePlayPause : undefined}
      />

      {/* Video Controls */}
      {isVideo(downloaded_path[currentIndex]) && (
        <button
          onClick={handleMuteToggle}
          className="absolute top-2 right-2 p-2 bg-gray-700 text-white rounded-full z-30"
        >
          {muted ? "🔇" : "🔊"}
        </button>
      )}

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 transform space-x-1">
        {downloaded_path.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 w-2 rounded-full ${
              currentIndex === index ? "bg-white" : "bg-gray-500"
            }`}
          ></button>
        ))}
      </div>
      </div>  {/* end Carousel Wrapper */}

      {/* Footer */}
      <div
        className="bg-white text-black p-2"
        style={{
          maxHeight: isCaptionExpanded ? '100px' : 'none',
          overflowY: isCaptionExpanded ? 'auto' : 'visible',
          msOverflowStyle: 'none',      // hide scrollbar in IE and Edge
          scrollbarWidth: 'none'        // hide scrollbar in Firefox
        }}
      >
        {!isCaptionExpanded && caption && caption.length > 100 ? (
          <p className="text-left">
            {caption.slice(0, 100)}...
            <button
              onClick={() => setIsCaptionExpanded(true)}
              className="font-semibold text-blue-500"
            >
              more
            </button>
          </p>
        ) : (
          <p className="text-left">
            {caption}
            {caption && caption.length > 100 && (
              <button
                onClick={() => setIsCaptionExpanded(false)}
                className="font-semibold text-blue-500"
              >
                less
              </button>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

const MediaComponent = React.forwardRef(({ src, alt, className, videoRef, muted, onManualUnmute, onClick, isPlaying, inView, refProp }, _) => {
  const ext = src.split('.').pop().toLowerCase();

  if (["mp4", "webm", "ogg"].includes(ext)) {
    return (
      <div className="relative" ref={refProp}>
        <video
          key={src}
          ref={videoRef}
          className={className}
          src={src}
          muted={muted}
          loop
          playsInline
          webkit-playsinline
          onClick={onClick}
        />
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-500 pointer-events-none ${inView ? (isPlaying ? "opacity-0" : "opacity-60") : "opacity-60"}`}
        ></div>
      </div>
    );
  } else {
    return (
      <div className="relative" ref={refProp}>
        <img className={className} src={src} alt={alt} loading="lazy" />
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-500 ${inView ? "opacity-0" : "opacity-60"}`}
        ></div>
      </div>
    );
  }
});

export default InstagramCarousel;